import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarPlus,
  Car,
  ChevronRight,
  CircleHelp,
  CircleUserRound,
  FileText,
  Heart,
  HeartPulse,
  Headphones,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPin,
  PackagePlus,
  PackageSearch,
  ReceiptText,
  RotateCcw,
  ShoppingBag,
  Store,
  Tags,
  TicketPercent,
  Truck,
  UserPlus,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { getDisplayNameForUI, maskPhoneForDisplay } from '../utils/displayName';

export const MyAccount = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const displayName = getDisplayNameForUI(user) || 'SparePe customer';
  const contact = user?.email || maskPhoneForDisplay(user?.phone) || 'Complete your profile';
  const initial = displayName.trim().charAt(0).toUpperCase() || 'S';

  const sections = [
    {
      title: 'Dashboard & services',
      items: [
        {
          label: 'My Dashboard',
          detail: 'View your dashboard overview',
          icon: LayoutDashboard,
          href: '/customer/dashboard',
        },
        {
          label: 'My Service Jobs',
          detail: 'View maintenance and repair jobs',
          icon: Wrench,
          href: '/customer/dashboard/jobs',
        },
        {
          label: 'Book Services',
          detail: 'Schedule a vehicle service',
          icon: CalendarPlus,
          href: '/service/booking',
        },
        {
          label: 'My Account',
          detail: 'View your profile and account',
          icon: UserRound,
          href: '/customer/dashboard/profile',
        },
        {
          label: 'Health Passport',
          detail: 'View your vehicle health history',
          icon: HeartPulse,
          href: '/health-passport',
        },
      ],
    },
    {
      title: 'Orders & account details',
      items: [
        {
          label: 'Order History',
          detail: 'View and track your purchases',
          icon: ShoppingBag,
          href: '/customer/dashboard/orders',
        },
        {
          label: 'My Wishlist',
          detail: 'View your saved products',
          icon: Heart,
          href: '/customer/dashboard/wishlist',
        },
        {
          label: 'Garage',
          detail: 'Manage your saved vehicles',
          icon: Car,
          href: '/customer/dashboard/vehicles',
        },
        {
          label: 'Addresses',
          detail: 'Manage delivery addresses',
          icon: MapPin,
          href: '/addresses',
        },
        {
          label: 'Company GST',
          detail: 'Manage company tax details',
          icon: ReceiptText,
          href: '/company_gst',
        },
        {
          label: 'Documents',
          detail: 'View and manage documents',
          icon: FileText,
          href: '/document',
        },
      ],
    },
    {
      title: 'Parts & marketplace',
      items: [
        {
          label: 'New Parts',
          detail: 'Browse new spare parts',
          icon: PackagePlus,
          href: '/catalog?spareType=new',
        },
        {
          label: 'Used Parts',
          detail: 'Browse verified used parts',
          icon: PackageSearch,
          href: '/catalog?spareType=used',
        },
        {
          label: 'Garage Services',
          detail: 'Find garages and services',
          icon: Store,
          href: '/garages',
        },
        {
          label: 'Brands',
          detail: 'Shop parts by brand',
          icon: Tags,
          href: '/brands',
        },
        {
          label: 'Deals',
          detail: 'View available offers',
          icon: TicketPercent,
          href: '/offers',
        },
        {
          label: 'Track Orders',
          detail: 'Check your delivery status',
          icon: Truck,
          href: '/myorder',
        },
      ],
    },
    {
      title: 'Partner with SparePe',
      items: [
        {
          label: 'List Your Garage',
          detail: 'Register your garage',
          icon: Building2,
          href: '/list-your-garage',
        },
        {
          label: 'Become Vendor',
          detail: 'Start selling on SparePe',
          icon: UserPlus,
          href: '/signup',
        },
      ],
    },
    {
      title: 'Help & support',
      items: [
        {
          label: 'FAQ',
          detail: 'Frequently asked questions',
          icon: CircleHelp,
          href: '/faq',
        },
        {
          label: 'Contact Us',
          detail: 'Get in touch with our team',
          icon: Mail,
          href: '/contact',
        },
        {
          label: 'Support',
          detail: 'Get help with your account',
          icon: Headphones,
          href: '/support',
        },
        {
          label: 'Return Policy',
          detail: 'Read return and refund terms',
          icon: RotateCcw,
          href: '/return-policy',
        },
      ],
    },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <main className="min-h-screen bg-[var(--sp-bg-deep)] px-3 pb-28 pt-4 text-[var(--sp-ink)] sm:px-6 sm:pt-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-5 pl-12 text-xl font-bold sm:pl-14 sm:text-2xl lg:mb-7 lg:text-3xl">Account</h1>

        <div className="lg:grid lg:grid-cols-[minmax(18rem,22rem)_minmax(0,1fr)] lg:items-start lg:gap-6 xl:gap-8">
          <aside className="lg:sticky lg:top-5">
            <section className="overflow-hidden rounded-3xl border border-[var(--sp-border)] bg-[var(--sp-bg-card)] shadow-[var(--sp-shadow)]">
              <button
                type="button"
                onClick={() => navigate('/customer/dashboard/profile')}
                className="flex w-full items-center gap-4 p-5 text-left hover:bg-[var(--sp-bg-surface)] lg:flex-col lg:items-center lg:text-center"
              >
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-blue-100 text-2xl font-bold text-blue-700 lg:h-24 lg:w-24 lg:text-4xl">
                  {initial}
                </span>
                <span className="min-w-0 flex-1 lg:w-full">
                  <span className="block truncate text-xl font-bold lg:text-2xl">{displayName}</span>
                  <span className="mt-0.5 block truncate text-sm text-[var(--sp-ink-2)]">{contact}</span>
                  <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[var(--sp-accent-dk)]">
                    View My Page <ChevronRight className="h-4 w-4" />
                  </span>
                </span>
              </button>
            </section>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-4 hidden w-full items-center justify-center gap-2 rounded-2xl border border-rose-300 bg-[var(--sp-bg-card)] px-4 py-4 font-bold text-rose-600 hover:bg-rose-500/10 lg:flex"
            >
              <LogOut className="h-5 w-5" />
              Log out
            </button>
          </aside>

          <div className="mt-5 grid gap-5 lg:mt-0 xl:grid-cols-2">
            {sections.map((section) => (
              <section
                key={section.title}
                className="overflow-hidden rounded-3xl border border-[var(--sp-border)] bg-[var(--sp-bg-card)] shadow-[var(--sp-shadow)]"
              >
                <h2 className="border-l-4 border-rose-500 px-4 py-4 text-lg font-bold">{section.title}</h2>
                <div className="px-4">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => navigate(item.href)}
                        className="flex w-full items-center gap-4 border-t border-[var(--sp-border)] py-4 text-left first:border-t-0 hover:text-[var(--sp-accent-dk)]"
                      >
                        <Icon className="h-5 w-5 shrink-0 text-[var(--sp-ink-3)]" />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium">{item.label}</span>
                          <span className="mt-0.5 block text-xs text-[var(--sp-ink-3)]">{item.detail}</span>
                        </span>
                        <ChevronRight className="h-5 w-5 shrink-0 text-[var(--sp-ink-3)]" />
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-300 bg-[var(--sp-bg-card)] px-4 py-4 font-bold text-rose-600 hover:bg-rose-500/10 lg:hidden"
        >
          <LogOut className="h-5 w-5" />
          Log out
        </button>

        <p className="mt-5 flex items-center justify-center gap-2 text-xs text-[var(--sp-ink-3)]">
          <CircleUserRound className="h-4 w-4" />
          SparePe customer account
        </p>
      </div>
    </main>
  );
};
